import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toastHelper } from '../util/toastHelper.js'

const API_VERSION = 'v1'
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api/${API_VERSION}/vendor`

// Get vendor details for review modal
export const getVendorDetailsForReview = createAsyncThunk(
	'review/getVendorDetails',
	async (vendorId) => {
		try {
			const res = await fetch(`${REQUEST_URL}/${vendorId}/details`, {
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error fetching vendor details:', err.message)
			throw err
		}
	}
)

// Get user's review for a specific proposal
export const getUserReviewForProposal = createAsyncThunk(
	'review/getUserReview',
	async (proposalId) => {
		try {
			const res = await fetch(`${REQUEST_URL}/review/proposal/${proposalId}`, {
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error fetching user review:', err.message)
			throw err
		}
	}
)

// Create a new review
export const createReview = createAsyncThunk(
	'review/createReview',
	async (reviewData) => {
		try {
			const res = await fetch(`${REQUEST_URL}/review`, {
				method: 'POST',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(reviewData),
			})
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error creating review:', err.message)
			throw err
		}
	}
)

// Update an existing review
export const updateReview = createAsyncThunk(
	'review/updateReview',
	async ({ reviewId, rating, comment }) => {
		try {
			const res = await fetch(`${REQUEST_URL}/review/${reviewId}`, {
				method: 'PATCH',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ rating, comment }),
			})
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error updating review:', err.message)
			throw err
		}
	}
)

// Delete a review
export const deleteReview = createAsyncThunk(
	'review/deleteReview',
	async (reviewId) => {
		try {
			const res = await fetch(`${REQUEST_URL}/review/${reviewId}`, {
				method: 'DELETE',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			})
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error deleting review:', err.message)
			throw err
		}
	}
)

// Get vendor reviews (public)
export const getVendorReviews = createAsyncThunk(
	'review/getVendorReviews',
	async ({ vendorId, page = 1, limit = 10 }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${vendorId}/reviews?page=${page}&limit=${limit}`,
				{
					method: 'GET',
					credentials: 'include',
					headers: {
						Accept: 'application/json',
						'Content-Type': 'application/json',
					},
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.error('Error fetching vendor reviews:', err.message)
			throw err
		}
	}
)

const initialState = {
	vendorDetails: null,
	currentReview: null,
	reviews: [],
	reviewStats: null,
	loading: false,
	error: null,
}

const reviewSlice = createSlice({
	name: 'review',
	initialState,
	reducers: {
		clearVendorDetails: (state) => {
			state.vendorDetails = null
		},
		clearCurrentReview: (state) => {
			state.currentReview = null
		},
	},
	extraReducers: (builder) => {
		builder
			// Get vendor details
			.addCase(getVendorDetailsForReview.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(getVendorDetailsForReview.fulfilled, (state, { payload }) => {
				state.loading = false
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch vendor details',
						'error'
					)
					state.error = payload?.message
					return
				}
				state.vendorDetails = payload.vendor
			})
			.addCase(getVendorDetailsForReview.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message
				toastHelper('Failed to fetch vendor details', 'error')
			})

			// Get user review for proposal
			.addCase(getUserReviewForProposal.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(getUserReviewForProposal.fulfilled, (state, { payload }) => {
				state.loading = false
				if (!payload || !payload.success) {
					if (payload?.message && payload.message !== 'No review found for this proposal') {
						toastHelper(
							payload?.message || 'Failed to fetch review',
							'error'
						)
					}
					state.error = payload?.message
					return
				}
				state.currentReview = payload.review
			})
			.addCase(getUserReviewForProposal.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message
			})

			// Create review
			.addCase(createReview.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(createReview.fulfilled, (state, { payload }) => {
				state.loading = false
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to create review',
						'error'
					)
					state.error = payload?.message
					return
				}
				toastHelper('Review submitted successfully', 'success')
				state.currentReview = payload.review
			})
			.addCase(createReview.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message
				toastHelper('Failed to create review', 'error')
			})

			// Update review
			.addCase(updateReview.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(updateReview.fulfilled, (state, { payload }) => {
				state.loading = false
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to update review',
						'error'
					)
					state.error = payload?.message
					return
				}
				toastHelper('Review updated successfully', 'success')
				state.currentReview = payload.review
			})
			.addCase(updateReview.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message
				toastHelper('Failed to update review', 'error')
			})

			// Delete review
			.addCase(deleteReview.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(deleteReview.fulfilled, (state, { payload }) => {
				state.loading = false
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to delete review',
						'error'
					)
					state.error = payload?.message
					return
				}
				toastHelper('Review deleted successfully', 'success')
				state.currentReview = null
			})
			.addCase(deleteReview.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message
				toastHelper('Failed to delete review', 'error')
			})

			// Get vendor reviews
			.addCase(getVendorReviews.pending, (state) => {
				state.loading = true
				state.error = null
			})
			.addCase(getVendorReviews.fulfilled, (state, { payload }) => {
				state.loading = false
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to fetch reviews',
						'error'
					)
					state.error = payload?.message
					return
				}
				state.reviews = payload.reviews
				state.reviewStats = payload.stats
			})
			.addCase(getVendorReviews.rejected, (state, action) => {
				state.loading = false
				state.error = action.error.message
				toastHelper('Failed to fetch reviews', 'error')
			})
	},
})

export const { clearVendorDetails, clearCurrentReview } = reviewSlice.actions

export default reviewSlice.reducer
